import { useState } from 'react';
import { AGENTS, getAgentColor, getAgentsByCSuite } from '../../utils/agents';
import { OrgNode } from './OrgNode';

const CEO = AGENTS.find((a) => a.slug === 'ceo-agent');
const C_SUITE = AGENTS.filter((a) => a.isCSuite && a.slug !== 'ceo-agent');

export function OrgTree() {
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => {
    const initial: Record<string, boolean> = {};
    C_SUITE.forEach((a) => {
      initial[a.slug] = true;
    });
    return initial;
  });

  function toggleExpand(slug: string) {
    setExpanded((prev) => ({ ...prev, [slug]: !prev[slug] }));
  }

  return (
    <div className="overflow-x-auto pb-4">
      <div className="flex flex-col items-center min-w-[900px]">
        {/* Owners */}
        <div className="flex items-center gap-6">
          <OrgNode
            name="Aaron"
            title="Co-Founder & Owner"
            department="C-Suite"
            colorClass="bg-gradient-to-br from-amber-500 to-purple-600"
            variant="owner"
          />
          <OrgNode
            name="Taylor"
            title="Co-Founder & Owner"
            department="C-Suite"
            colorClass="bg-gradient-to-br from-amber-500 to-purple-600"
            variant="owner"
          />
        </div>

        {/* Connector: Owners to CEO */}
        <div className="w-px h-6 bg-border-subtle" />

        {/* CEO */}
        {CEO && (
          <OrgNode
            name={CEO.name}
            title={CEO.title}
            shortName={CEO.shortName}
            department={CEO.department}
            colorClass={getAgentColor(CEO.department)}
            variant="csuite"
          />
        )}

        {/* Connector: CEO to C-Suite bar */}
        <div className="w-px h-6 bg-border-subtle" />
        <div
          className="border-t border-border-subtle"
          style={{ width: `${(C_SUITE.length - 1) * 220}px` }}
        />

        {/* C-Suite row + their reports */}
        <div className="flex justify-center gap-0">
          {C_SUITE.map((exec) => {
            const reports = getAgentsByCSuite(exec.slug);
            const isOpen = expanded[exec.slug] ?? true;

            return (
              <div key={exec.slug} className="flex flex-col items-center" style={{ minWidth: '220px' }}>
                {/* Vertical line from horizontal bar down to exec node */}
                <div className="w-px h-6 bg-border-subtle" />

                {/* C-Suite node */}
                <OrgNode
                  name={exec.name}
                  title={exec.title}
                  shortName={exec.shortName}
                  department={exec.department}
                  colorClass={getAgentColor(exec.department)}
                  variant="csuite"
                  isCollapsible={reports.length > 0}
                  isExpanded={isOpen}
                  onToggle={() => toggleExpand(exec.slug)}
                />

                {/* Department agents */}
                {isOpen && reports.length > 0 ? (
                  <div className="flex flex-col items-center mt-1">
                    {/* Connector line down from exec */}
                    <div className="w-px h-4 bg-border-subtle" />

                    {/* Agent list column */}
                    <div className="flex flex-col items-center gap-0">
                      {reports.map((agent, idx) => (
                        <div key={agent.slug} className="flex items-center">
                          {/* Left connector stub */}
                          <div className="flex flex-col items-center">
                            {idx > 0 ? (
                              <div className="w-px h-2 bg-border-subtle" />
                            ) : null}
                            <div className="flex items-center gap-2 py-1">
                              <div className="w-4 border-t border-border-subtle" />
                              <div className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-bg-tertiary transition-colors">
                                {/* Mini avatar */}
                                <div
                                  className={`w-6 h-6 rounded-full flex items-center justify-center text-[9px] font-semibold text-white flex-shrink-0 ${getAgentColor(agent.department)}`}
                                >
                                  {getInitials(agent.name)}
                                </div>
                                <div className="text-left">
                                  <p className="text-[11px] font-medium text-text-primary leading-tight whitespace-nowrap">
                                    {agent.name}
                                  </p>
                                  <p className="text-[9px] text-text-muted leading-tight whitespace-nowrap">
                                    {agent.shortName}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function getInitials(name: string): string {
  const parts = name.split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export default OrgTree;
